import { WorkBook, utils } from 'xlsx';
import { IFormInputSpec } from 'pages/revisions-module/template-editor/editors/form/types';

const DATE_CELL_FORMAT = 'dd"."mm"."yyyy';
const FAULTS_COLUMN_KEY = '_faults';

interface MeasurementExportColumn {
  key: string;
  header: string;
  spec?: IFormInputSpec;
}

export interface BuildMeasurementWorkbookParams {
  rows: readonly object[];
  spec: Readonly<IFormInputSpec[]>;
  columnKeys: readonly string[];
}

/**
 * Converts a stored calendar date to a local Date without applying a timezone offset.
 */
function parseCalendarDate(value: unknown): Date | undefined {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return undefined;
    }

    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) {
    return undefined;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return undefined;
  }

  return date;
}

/**
 * Preserves a value as text unless its form specification guarantees a native spreadsheet type.
 */
function convertValueForExport(value: unknown, spec: IFormInputSpec): string | number | boolean | Date | null {
  if (value === null || value === undefined) {
    return null;
  }

  switch (spec.type) {
    case 'number':
      return typeof value === 'number' && Number.isFinite(value) ? value : String(value);

    case 'checkbox':
      return typeof value === 'boolean' ? value : String(value);

    case 'date':
      return parseCalendarDate(value) ?? String(value);

    case 'text':
    case 'select':
    default:
      return String(value);
  }
}

/**
 * Resolves selected export columns in the same order in which ExportModal supplies them.
 */
function resolveExportColumns(
  spec: Readonly<IFormInputSpec[]>,
  columnKeys: readonly string[]
): MeasurementExportColumn[] {
  const specByName = new Map(spec.map((columnSpec) => [columnSpec.name, columnSpec]));

  return columnKeys.flatMap((key) => {
    if (key === FAULTS_COLUMN_KEY) {
      return [{ key, header: 'Nedostatky' }];
    }

    const columnSpec = specByName.get(key);

    if (!columnSpec) {
      return [];
    }

    return [
      {
        key,
        header: columnSpec.required ? `${columnSpec.label} (povinné)` : columnSpec.label,
        spec: columnSpec,
      },
    ];
  });
}

/**
 * Builds a typed XLSX workbook for device measurements without lossy CSV type inference.
 */
export default function buildMeasurementWorkbook({ rows, spec, columnKeys }: BuildMeasurementWorkbookParams): WorkBook {
  const columns = resolveExportColumns(spec, columnKeys);
  const data = [
    columns.map(({ header }) => header),
    ...rows.map((row) => {
      const values = row as Record<string, unknown>;

      return columns.map(({ key, spec: columnSpec }) => {
        if (key === FAULTS_COLUMN_KEY) {
          return JSON.stringify(values[FAULTS_COLUMN_KEY] ?? []);
        }

        return convertValueForExport(values[key], columnSpec!);
      });
    }),
  ];
  const worksheet = utils.aoa_to_sheet(data, {
    cellDates: true,
    dateNF: DATE_CELL_FORMAT,
  });

  worksheet['!cols'] = columns.map(() => ({ wch: 15 }));

  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  return workbook;
}
